import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faCheckCircle, 
  faSpinner, 
  faUser,
  faSearch,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import toastService from '@frontend/AppSettings/services/ToastService';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import './AssignLeadModal.css';

interface AssignLeadModalProps {
  onClose: () => void;
  stakeholderId: string;
  type: 'individual' | 'organization';
  stakeholderData?: any;
  onLeadAssigned?: () => void;
  currentAssignedLeadId?: string | null;
}

const AssignLeadModal: React.FC<AssignLeadModalProps> = ({ 
  onClose, 
  stakeholderId, 
  type,
  stakeholderData,
  onLeadAssigned,
  currentAssignedLeadId,
}) => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(currentAssignedLeadId || null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const employeesData = await EmployeeService.getEmployees();
        setEmployees(employeesData);
      } catch (error) {
        console.error('Error fetching employees:', error);
        setError('Failed to load employees. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  const handleClose = () => {
    setSearchTerm('');
    setSelectedEmployeeId(currentAssignedLeadId || null);
    setError('');
    onClose();
  };

  const handleEmployeeSelect = (employeeId: string) => {
    setSelectedEmployeeId(employeeId === selectedEmployeeId ? null : employeeId);
  };

  const handleAssignLead = async () => {
    if (!selectedEmployeeId) {
      setError('Please select an employee to assign as lead.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Resolve client vs agent: try client first, then agent (lead assignment only for clients)
      let clientData: any = null;
      let agentData: any = null;
      try {
        clientData = await ClientService.getClientById(stakeholderId);
      } catch {
        clientData = null;
      }
      if (!clientData) {
        try {
          agentData = await AgentsService.getAgent(stakeholderId);
        } catch {
          agentData = null;
        }
      }

      if (agentData && !clientData) {
        setError('Lead assignment is only available for client contacts.');
        setSaving(false);
        return;
      }

      if (!clientData) {
        setError('Stakeholder not found.');
        setSaving(false);
        return;
      }

      // Helper function to safely get field value from client data (match AddActivityPage)
      const getFieldValue = (field: string, isDateField: boolean = false) => {
        const snakeValue = (clientData as any)[field];
        const pascalKey = field.split('_').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
        const capitalizedKey = field.charAt(0).toUpperCase() + field.slice(1);
        const pascalValue = (clientData as any)[pascalKey];
        const capitalizedValue = (clientData as any)[capitalizedKey];
        const value = snakeValue !== undefined && snakeValue !== null
          ? snakeValue
          : pascalValue !== undefined && pascalValue !== null
          ? pascalValue
          : capitalizedValue;

        if (isDateField) {
          if (value === '' || value === null || value === undefined) {
            return null;
          }
        }

        return value !== null && value !== undefined ? value : '';
      };

      // Prepare update data
      const updateData: any = {
        FirstName: getFieldValue('first_name'),
        LastName: getFieldValue('last_name'),
        Company: getFieldValue('company'),
        Email: getFieldValue('email'),
        Phone: getFieldValue('phone'),
        Address: getFieldValue('address'),
        Type: getFieldValue('type'),
        ContactName: getFieldValue('contact_name'),
        Prefix: getFieldValue('prefix'),
        MiddleName: getFieldValue('middle_name'),
        DateOfBirth: getFieldValue('date_of_birth', true),
        SecondaryPhoneNumber: getFieldValue('secondary_phone_number'),
        Entity: getFieldValue('entity'),
        Title: getFieldValue('title'),
        ContactCreatedDate: getFieldValue('contact_created_date', true),
        PrimaryAddressCity: getFieldValue('primary_address_city'),
        PrimaryAddressProvince: getFieldValue('primary_address_province'),
        PrimaryAddressPostalZip: getFieldValue('primary_address_postal_zip'),
        PrimaryAddressCountry: getFieldValue('primary_address_country'),
        SecondaryEmailAddress: getFieldValue('secondary_email_address'),
        SecondaryAddressStreet: getFieldValue('secondary_address_street'),
        SecondaryAddressCity: getFieldValue('secondary_address_city'),
        SecondaryAddressProvinceState: getFieldValue('secondary_address_province_state'),
        SecondaryAddressPostalZip: getFieldValue('secondary_address_postal_zip'),
        SecondaryAddressCountry: getFieldValue('secondary_address_country'),
        FormalNameIfDifferent: getFieldValue('formal_name_if_different'),
        Notes: getFieldValue('notes'),
        RecentActivity: getFieldValue('recent_activity'),
        AssignedLead: selectedEmployeeId, // Update assigned lead
      };

      // Use updateClient endpoint to update the assigned_lead field
      await ClientService.updateClient(stakeholderId, updateData);
      
      toastService.success('Lead assigned successfully');

      // Trigger refresh callback if provided
      if (onLeadAssigned) {
        onLeadAssigned();
      }

      // Close modal
      handleClose();
    } catch (error: any) {
      console.error('Error assigning lead:', error);
      setError(error.message || 'Failed to assign lead. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLead = async () => {
    setSaving(true);
    setError('');

    try {
      let clientData: any = null;
      try {
        clientData = await ClientService.getClientById(stakeholderId);
      } catch {
        clientData = null;
      }
      if (!clientData) {
        try {
          await AgentsService.getAgent(stakeholderId);
        } catch {
          /* not found */
        }
        setError('Lead assignment is only available for client contacts.');
        setSaving(false);
        return;
      }

      // Helper function to safely get field value
      const getFieldValue = (field: string, isDateField: boolean = false) => {
        const snakeValue = (clientData as any)[field];
        const pascalKey = field.split('_').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
        const capitalizedKey = field.charAt(0).toUpperCase() + field.slice(1);
        const pascalValue = (clientData as any)[pascalKey];
        const capitalizedValue = (clientData as any)[capitalizedKey];
        const value = snakeValue !== undefined && snakeValue !== null
          ? snakeValue
          : pascalValue !== undefined && pascalValue !== null
          ? pascalValue
          : capitalizedValue;

        if (isDateField) {
          if (value === '' || value === null || value === undefined) {
            return null;
          }
        }

        return value !== null && value !== undefined ? value : '';
      };

      // Prepare update data with null assigned_lead
      const updateData: any = {
        FirstName: getFieldValue('first_name'),
        LastName: getFieldValue('last_name'),
        Company: getFieldValue('company'),
        Email: getFieldValue('email'),
        Phone: getFieldValue('phone'),
        Address: getFieldValue('address'),
        Type: getFieldValue('type'),
        ContactName: getFieldValue('contact_name'),
        Prefix: getFieldValue('prefix'),
        MiddleName: getFieldValue('middle_name'),
        DateOfBirth: getFieldValue('date_of_birth', true),
        SecondaryPhoneNumber: getFieldValue('secondary_phone_number'),
        Entity: getFieldValue('entity'),
        Title: getFieldValue('title'),
        ContactCreatedDate: getFieldValue('contact_created_date', true),
        PrimaryAddressCity: getFieldValue('primary_address_city'),
        PrimaryAddressProvince: getFieldValue('primary_address_province'),
        PrimaryAddressPostalZip: getFieldValue('primary_address_postal_zip'),
        PrimaryAddressCountry: getFieldValue('primary_address_country'),
        SecondaryEmailAddress: getFieldValue('secondary_email_address'),
        SecondaryAddressStreet: getFieldValue('secondary_address_street'),
        SecondaryAddressCity: getFieldValue('secondary_address_city'),
        SecondaryAddressProvinceState: getFieldValue('secondary_address_province_state'),
        SecondaryAddressPostalZip: getFieldValue('secondary_address_postal_zip'),
        SecondaryAddressCountry: getFieldValue('secondary_address_country'),
        FormalNameIfDifferent: getFieldValue('formal_name_if_different'),
        Notes: getFieldValue('notes'),
        RecentActivity: getFieldValue('recent_activity'),
        AssignedLead: null, // Remove assigned lead
      };

      await ClientService.updateClient(stakeholderId, updateData);
      
      toastService.success('Lead removed successfully');

      if (onLeadAssigned) {
        onLeadAssigned();
      }

      handleClose();
    } catch (error: any) {
      console.error('Error removing lead:', error);
      setError(error.message || 'Failed to remove lead. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Filter employees based on search term
  const filteredEmployees = employees.filter((employee) => {
    if (!searchTerm) return true;
    const searchStr = searchTerm.toLowerCase();
    const fullName = `${employee.FirstName || ''} ${employee.LastName || ''}`.trim().toLowerCase();
    const email = (employee.Email || '').toLowerCase();
    const employeeId = (employee.EmployeeID || '').toLowerCase();
    
    return fullName.includes(searchStr) || 
           email.includes(searchStr) || 
           employeeId.includes(searchStr);
  });

  return (
    <div className="assign-lead-overlay" onClick={handleClose}>
      <div className="assign-lead-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="assign-lead-header">
          <div className="assign-lead-header-left">
            <div className="assign-lead-title-row">
              <div className="assign-lead-header-icon">
                <FontAwesomeIcon icon={faUser} />
              </div>
              <div className="assign-lead-header-text">
                <h2 className="assign-lead-title">Assign SEPT Lead</h2>
                <p className="assign-lead-subtitle">Select an employee to assign as the relationship manager</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="assign-lead-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="assign-lead-content">
          {/* Search */}
          <div className="assign-lead-search-wrapper">
            <FontAwesomeIcon icon={faSearch} className="assign-lead-search-icon" />
            <input
              type="text"
              className="assign-lead-search-input"
              placeholder="Search by name, email, or employee ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="assign-lead-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading employees...</span>
            </div>
          ) : (
            <div className="assign-lead-employees-list">
              {filteredEmployees.length === 0 ? (
                <div className="assign-lead-empty">
                  <p>No employees found</p>
                </div>
              ) : (
                filteredEmployees.map((employee) => {
                  const fullName = `${employee.FirstName || ''} ${employee.LastName || ''}`.trim() || 'Unknown';
                  const isSelected = selectedEmployeeId === employee.EmployeeID;
                  const isCurrentLead = currentAssignedLeadId === employee.EmployeeID;
                  
                  return (
                    <button
                      key={employee.EmployeeID}
                      type="button"
                      className={`assign-lead-employee-item ${isSelected ? 'assign-lead-employee-item-selected' : ''} ${isCurrentLead ? 'assign-lead-employee-item-current' : ''}`}
                      onClick={() => handleEmployeeSelect(employee.EmployeeID)}
                    >
                      <div className="assign-lead-employee-avatar">
                        {getInitials(fullName)}
                      </div>
                      <div className="assign-lead-employee-info">
                        <div className="assign-lead-employee-name">
                          {fullName}
                          {isCurrentLead && (
                            <span className="assign-lead-current-badge">Current</span>
                          )}
                        </div>
                        <div className="assign-lead-employee-details">
                          <span className="assign-lead-employee-email">{employee.Email || 'No email'}</span>
                          <span className="assign-lead-employee-id">ID: {employee.EmployeeID}</span>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="assign-lead-employee-check">
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}

          {error && <div className="assign-lead-error">{error}</div>}
        </div>

        {/* Footer Actions */}
        <div className="assign-lead-footer">
          <div className="assign-lead-footer-info">
            <FontAwesomeIcon icon={faInfoCircle} className="assign-lead-footer-info-icon" />
            <span>Assigned lead will be visible to your team</span>
          </div>
          <div className="assign-lead-footer-left">
            {currentAssignedLeadId && (
              <button
                type="button"
                className="assign-lead-button assign-lead-button-remove"
                onClick={handleRemoveLead}
                disabled={saving}
              >
                Remove Lead
              </button>
            )}
          </div>
          <div className="assign-lead-footer-buttons">
            <button
              type="button"
              className="assign-lead-button assign-lead-button-cancel"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="assign-lead-button assign-lead-button-primary"
              onClick={handleAssignLead}
              disabled={saving || !selectedEmployeeId}
            >
              {saving ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Assign Lead
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignLeadModal;
